import { apiService } from './ApiService';

// ── Client-side in-memory cache ───────────────────────────────────────────────
// Keyed by the first 64 chars of the base64 payload — enough to be unique in practice.
const _cache = new Map<string, string>();

/**
 * Transcribes a base64-encoded audio clip via the backend Gemini endpoint.
 * Results are cached in memory so repeated calls for the same clip are free.
 * @param audioData  - base64 string or data-URL (e.g. "data:audio/webm;base64,...")
 * @param mimeType   - MIME type such as "audio/webm" or "audio/ogg"
 * @param sessionId  - when provided the transcript is persisted in the session state
 *                     so it survives page reloads
 * @param language   - BCP-47 language code (e.g. "en", "es", "he") passed to the
 *                     transcription model as a hint
 * @returns The transcript text, or null on failure.
 */
export async function transcribeAudio(
  audioData: string,
  mimeType: string,
  sessionId?: string,
  language?: string,
): Promise<string | null> {
  try {
    // Strip data-URL prefix if present
    const base64 = audioData.startsWith('data:')
      ? audioData.split(',')[1]
      : audioData;

    // Use the first 64 chars as a cheap cache key (same audio → same prefix)
    const cacheKey = base64.slice(0, 64);
    if (_cache.has(cacheKey)) {
      return _cache.get(cacheKey)!;
    }

    const response = await apiService.post<{ transcript: string }>(
      '/api/chat/transcribe',
      { audio_data: base64, mime_type: mimeType, session_id: sessionId ?? null, language: language ?? null },
    );

    const transcript = response.data.transcript || null;
    if (transcript) {
      _cache.set(cacheKey, transcript);
    }
    return transcript;
  } catch (err) {
    console.error('Transcription error:', err);
    return null;
  }
}
